import { X, Cpu, Brain, Wrench, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { AgentTemplate } from '@/schemas/agent-template'
import { getCategoryLabel, getCategoryColorClass } from '@/schemas/agent-template'

interface TemplatePreviewProps {
  template: AgentTemplate
  onClose: () => void
  onUseTemplate: () => void
}

export function TemplatePreview({
  template,
  onClose,
  onUseTemplate,
}: TemplatePreviewProps) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl border border-gray-700/50"
        style={{ backgroundColor: '#0d0d14' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-700/50">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-indigo-500/20 text-3xl flex-shrink-0">
              {template.icon}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                {template.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                    getCategoryColorClass(template.category)
                  )}
                >
                  {getCategoryLabel(template.category)}
                </span>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-400 hover:text-white h-8 w-8 flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
          {/* Description */}
          {template.description && (
            <div className="mb-6">
              <p className="text-sm text-gray-300">{template.description}</p>
            </div>
          )}

          {/* Configuration Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* LLM Config */}
            <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="h-4 w-4 text-indigo-400" />
                <h4 className="text-sm font-medium text-white">Modelo LLM</h4>
              </div>
              <div className="space-y-1 text-xs text-gray-400">
                <p>
                  <span className="text-gray-500">Provider:</span>{' '}
                  {template.llm_config?.provider || 'Nao definido'}
                </p>
                <p>
                  <span className="text-gray-500">Modelo:</span>{' '}
                  {template.llm_config?.model || 'Nao definido'}
                </p>
                <p>
                  <span className="text-gray-500">Temperatura:</span>{' '}
                  {template.llm_config?.temperature ?? 0.7}
                </p>
                <p>
                  <span className="text-gray-500">Max Tokens:</span>{' '}
                  {template.llm_config?.max_tokens ?? 2048}
                </p>
              </div>
            </div>

            {/* Memory Config */}
            <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="h-4 w-4 text-purple-400" />
                <h4 className="text-sm font-medium text-white">Memoria</h4>
              </div>
              <div className="space-y-1 text-xs text-gray-400">
                <p>
                  <span className="text-gray-500">Tipo:</span>{' '}
                  {template.memory_config?.type || 'Nao definido'}
                </p>
                {template.memory_config?.window_size && (
                  <p>
                    <span className="text-gray-500">Janela:</span>{' '}
                    {template.memory_config.window_size} mensagens
                  </p>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-yellow-400" />
                <h4 className="text-sm font-medium text-white">Configuracoes</h4>
              </div>
              <div className="space-y-1 text-xs text-gray-400">
                <p>
                  <span className="text-gray-500">Timeout:</span>{' '}
                  {template.settings?.timeout ?? 60}s
                </p>
                <p>
                  <span className="text-gray-500">Max Retries:</span>{' '}
                  {template.settings?.max_retries ?? 3}
                </p>
                <p>
                  <span className="text-gray-500">Max Iterations:</span>{' '}
                  {template.settings?.max_iterations ?? 10}
                </p>
              </div>
            </div>

            {/* Suggested Tools */}
            <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="h-4 w-4 text-green-400" />
                <h4 className="text-sm font-medium text-white">Tools Sugeridas</h4>
              </div>
              <div className="flex flex-wrap gap-1">
                {template.suggested_tools?.length > 0 ? (
                  template.suggested_tools.map((tool, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-gray-700 text-gray-300"
                    >
                      {tool}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">Nenhuma sugerida</span>
                )}
              </div>
            </div>
          </div>

          {/* System Prompt */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-white mb-2">System Prompt</h4>
            <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                {template.system_prompt}
              </pre>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-700/50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-gray-700 text-gray-300 hover:bg-white/10"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onUseTemplate}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
          >
            Usar Template
          </Button>
        </div>
      </div>
    </div>
  )
}
